import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { PurchaseRequisitionView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { decidePurchaseRequisition, purchaseRequisitionQuery } from '../api/procurement.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { LoadFailed, OfflineNote, ReasonDialog } from '../procurement/common.tsx';
import { documentLabel, documentTag } from '../procurement/format.ts';
import { formatInPacks } from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

// One purchase requisition (docs/plans/phase2-procurement.md §5.3).

const route = getRouteApi('/_app/procurement/requisitions/$requisitionId');

type Decision = 'approve' | 'reject' | 'cancel';
const WORDS: Record<Decision, { confirm: string; toast: string }> = {
  approve: { confirm: 'Approve', toast: 'Purchase approved' },
  reject: { confirm: 'Reject', toast: 'Purchase rejected' },
  cancel: { confirm: 'Cancel requisition', toast: 'Requisition cancelled' },
};

export function PurchaseRequisitionPage() {
  const { requisitionId } = route.useParams();
  const req = useQuery(purchaseRequisitionQuery(requisitionId));
  if (req.isPending) return <LoadingBlock label="Loading the requisition" rows={4} />;
  if (req.isError)
    return (
      <LoadFailed
        error={req.error}
        fallback="The purchase requisition couldn't be loaded."
        onRetry={() => void req.refetch()}
      />
    );
  return <RequisitionDetail req={req.data} />;
}

function RequisitionDetail({ req }: { req: PurchaseRequisitionView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const [deciding, setDeciding] = useState<Decision | null>(null);
  const decide = useMutation({
    mutationFn: ({ decision, reason }: { decision: Decision; reason: string }) =>
      decidePurchaseRequisition(req.id, decision, reason),
    onSuccess: async (result, { decision }) => {
      queryClient.setQueryData(purchaseRequisitionQuery(req.id).queryKey, result);
      await queryClient.invalidateQueries({ queryKey: ['procurement', 'requisitions'] });
      toasts.show({ title: WORDS[decision].toast, description: result.number });
      setDeciding(null);
    },
  });
  const mine = session?.user.id === req.requestedBy.id;
  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/procurement/requisitions">Back to purchase requisitions</Link>
      </p>
      <PageHeader title={`Purchase requisition ${req.number}`} />
      <Section
        title={`For ${req.store.name}`}
        actions={<Tag state={documentTag(req.status)}>{documentLabel(req.status)}</Tag>}
      >
        <dl className={styles.facts}>
          <dt>Requisition</dt>
          <dd>
            <span className="wodi-id">{req.number}</span>
          </dd>
          <dt>Asked by</dt>
          <dd>
            {req.requestedBy.name}, {formatInstant(req.requestedAt)}
          </dd>
          {req.note === null ? null : (
            <>
              <dt>Note</dt>
              <dd>{req.note}</dd>
            </>
          )}
          {req.decisionReason === null ? null : (
            <>
              <dt>Decision</dt>
              <dd>
                {req.decidedBy?.name ?? req.requestedBy.name}: {req.decisionReason}
              </dd>
            </>
          )}
        </dl>
        <div className={styles.actions}>
          {req.canDecide ? (
            <>
              <Button
                variant="primary"
                isDisabled={!online}
                onPress={() => {
                  setDeciding('approve');
                }}
              >
                Approve
              </Button>
              <Button
                isDisabled={!online}
                onPress={() => {
                  setDeciding('reject');
                }}
              >
                Reject
              </Button>
            </>
          ) : null}
          {req.status === 'requested' && mine ? (
            <>
              <span className={styles.meta}>
                Another person with approval rights must approve this.
              </span>
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  setDeciding('cancel');
                }}
              >
                Cancel requisition
              </Button>
            </>
          ) : null}
          {req.status === 'approved' && session !== null && can(session, 'procurement.order') ? (
            <Link to="/procurement/orders/new" search={{ requisitionId: req.id }}>
              Prepare LPO
            </Link>
          ) : null}
        </div>
        <OfflineNote what="Deciding a requisition" />
        {req.orders.length === 0 ? null : (
          <p className={styles.meta}>
            LPOs:{' '}
            {req.orders.map((o, i) => (
              <span key={o.id}>
                {i === 0 ? '' : ', '}
                <Link to="/procurement/orders/$orderId" params={{ orderId: o.id }}>
                  <span className="wodi-id">{o.number}</span>
                </Link>
              </span>
            ))}
          </p>
        )}
      </Section>
      <Section title="Lines">
        <TableScroll>
          <Table flat aria-label={`Lines of ${req.number}`}>
            <TableHeader>
              <Column isRowHeader>Item</Column>
              <Column numeric>Quantity</Column>
              <Column>Note</Column>
            </TableHeader>
            <TableBody items={req.lines.map((l) => ({ ...l, id: l.lineNo }))}>
              {(l) => (
                <Row id={l.lineNo}>
                  <Cell>{l.itemName}</Cell>
                  <Cell numeric>
                    {formatInPacks(l.quantity, l.baseUnitDisplay, {
                      display: l.packUnitDisplay,
                      baseUnitsPerPack: l.baseUnitsPerPack,
                    })}
                  </Cell>
                  <Cell>{l.note ?? ''}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      {deciding === null ? null : (
        <ReasonDialog
          title={`${WORDS[deciding].confirm} ${req.number}`}
          confirmLabel={WORDS[deciding].confirm}
          isPending={decide.isPending}
          error={decide.error}
          onConfirm={(reason) => {
            decide.mutate({ decision: deciding, reason });
          }}
          onClose={() => {
            setDeciding(null);
            decide.reset();
          }}
        />
      )}
    </div>
  );
}
